import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { supabase } from '@/lib/supabase';

export function SubmitPage() {
  const [step, setStep] = useState<'otp' | 'upload' | 'done'>('otp');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // OTP state
  const [otpCode, setOtpCode] = useState('');
  const [assignmentId, setAssignmentId] = useState<string | null>(null);

  // Upload state
  const [studentName, setStudentName] = useState('');
  const [studentClass, setStudentClass] = useState('');
  const [uploadedPaths, setUploadedPaths] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      setError('請輸入 6 位數提交碼');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/assignments/by-code/${otpCode}`);
      if (!response.ok) {
        throw new Error('提交碼無效或已過期');
      }

      const data = await response.json();
      setAssignmentId(data.assignmentId);
      setStep('upload');
    } catch (err) {
      setError(err instanceof Error ? err.message : '驗證失敗');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    const newPaths: string[] = [];

    for (const file of acceptedFiles) {
      const fileExt = file.name.split('.').pop();
      const fileName = `submissions/${otpCode}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('assignment-images')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (!uploadError) {
        newPaths.push(fileName);
      } else {
        console.error('Upload error:', uploadError);
      }
    }

    setUploadedPaths((prev) => [...prev, ...newPaths]);
    setUploading(false);
  }, [otpCode]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.heic'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 10,
    disabled: uploading,
  });

  const handleSubmit = async () => {
    if (!assignmentId || !studentName.trim() || uploadedPaths.length === 0) {
      setError('請填寫姓名並上傳作業');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/assignments/${assignmentId}/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submitCode: otpCode,
          studentName: studentName.trim(),
          studentClass: studentClass.trim() || undefined,
          imagePaths: uploadedPaths,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '提交失敗');
      }

      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ maxWidth: 520, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} textAlign="center" gutterBottom>
            提交作業
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Step 1: OTP */}
          {step === 'otp' && (
            <Stack spacing={3} alignItems="center">
              <Typography variant="body2" color="text.secondary" textAlign="center">
                請輸入老師提供的 6 位數提交碼
              </Typography>
              <TextField
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 32, letterSpacing: 12, fontWeight: 700 } }}
                sx={{ width: 260 }}
              />
              <Button variant="contained" size="large" fullWidth disabled={otpCode.length !== 6 || loading} onClick={handleVerifyOtp}>
                {loading ? <CircularProgress size={24} /> : '驗證'}
              </Button>
            </Stack>
          )}

          {/* Step 2: Upload */}
          {step === 'upload' && (
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                請填寫資料並上傳作業
              </Typography>

              <TextField
                label="姓名"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="班級（選填）"
                value={studentClass}
                onChange={(e) => setStudentClass(e.target.value)}
                fullWidth
              />

              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                }}
              >
                <input {...getInputProps()} />
                {uploading ? (
                  <CircularProgress size={32} />
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2">
                      {isDragActive ? '放開以上傳' : '點擊或拖放檔案到此處'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      支援圖片及 PDF
                    </Typography>
                  </>
                )}
              </Box>

              {uploadedPaths.length > 0 && (
                <Typography variant="body2" color="success.main">
                  已上傳 {uploadedPaths.length} 個檔案
                </Typography>
              )}

              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={loading || !studentName.trim() || uploadedPaths.length === 0}
                onClick={handleSubmit}
              >
                {loading ? <CircularProgress size={24} /> : '提交作業'}
              </Button>
            </Stack>
          )}

          {/* Step 3: Done */}
          {step === 'done' && (
            <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
              <Typography variant="h6" fontWeight={700} color="success.main">
                提交成功！
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                你的作業已成功提交，老師將會批改你的作業。
              </Typography>
              <Button variant="outlined" onClick={() => { setStep('otp'); setOtpCode(''); setUploadedPaths([]); setStudentName(''); setStudentClass(''); }}>
                提交另一份作業
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
